import React, { useState } from 'react';
import Icon from './icons';
import { getStore, getUp, getUpKey, getTxt, getChk, mutStore, FormDl, MasterCollapse, MasterChip, UpLabel, renameUpload, dispName } from './stepcommon';
import MasterPanel from './MasterPanel';
import { coVal, coUploadId } from './collect';
import { s3upload, bondZip } from './api';
import type { BondZipAttach, BondZipBondDoc } from './api';
import { getBondVers, BOND_KEYS } from './bonddoc';
import { downloadUploadFile, downloadCollectFile } from './docgen';
import type { Project, CoCard, StoreKey } from './types';
import BondForm from './BondForm';
import DocFieldModal from './DocFieldModal';
import { fileUrl } from './api';
import BondDocRow from './BondDocRow';
import CoStatBar from './CoStatBar';
import { coStat } from './costep';
import { saveToProject, savedMsg } from './projectFolder';

interface Props {
  n: 8 | 9 | 10; // 11~14는 Steps11to14 (D배치)
  p: Project;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
  masterOpen: boolean;
  onToggleMaster: () => void;
  onFilePv: (name: string, title?: string) => void;
}

function colCls(n: number): string { return n >= 3 ? 'cols3' : (n === 2 ? 'cols2' : 'cols1'); }

export default function Steps8to11({ n, p, onPatch, toast, masterOpen, onToggleMaster, onFilePv }: Props) {
  const collect = p.collect;
  const store = ('s' + n) as StoreKey;
  const [zipBusy, setZipBusy] = useState('');
  const [openCo, setOpenCo] = useState<Record<string, boolean>>({});
  const [regView, setRegView] = useState(-1);   // 등록원부 보며 적기 — 지금 적는 칸(0 등록번호 · 1 등록일자) · -1 닫힘 (2026-11-01)
  const foldOf = (id: string): boolean => coStat(p, id, n) === 'done' && !openCo[id];
  const toggleFold = (id: string) => setOpenCo((o) => ({ ...o, [id]: !o[id] }));
  // 구비서류 슬롯 → 연동 매핑키 + ZIP 파일명용 라벨 (발행등기 ZIP 첨부에 사용)
  const ZIP_SLOTS: [string, string, string][] = [
    ['biz', 'co_biz', '사업자등록증'], ['rule', 'co_rule', '정관'], ['reg', 'co_reg', '법인등기부등본'],
    ['fin', 'co_fin', '최근연도재무제표'], ['roster', 'co_roster', '주주명부'],
  ];
  const buildAttach = (co: CoCard): BondZipAttach[] => {
    const clean = (s: string): string => (s || '').replace(/[/\\?%*:|"<>]/g, '').replace(/\s+/g, ' ').trim();
    const out: BondZipAttach[] = [];
    for (let i = 0; i < ZIP_SLOTS.length; i++) {
      const slot = ZIP_SLOTS[i][0]; const mapKey = ZIP_SLOTS[i][1]; const label = ZIP_SLOTS[i][2];
      const upName = getUp(p, store, co.id + '_' + slot);
      const upKey = getUpKey(p, store, co.id + '_' + slot);
      const linked = upName ? '' : (collect ? coVal(collect, co, mapKey) : '');
      const srcName = upName || linked;
      if (!srcName) continue;
      const m = srcName.match(/\.[A-Za-z0-9]{1,5}$/);
      const zipName = clean(label) + '_' + clean(co.name || '법인') + (m ? m[0] : '');
      if (upName && upKey) out.push({ name: zipName, s3Key: upKey });
      else if (linked && collect) { const id = coUploadId(collect, linked); if (id) out.push({ name: zipName, uploadId: id }); }
    }
    return out;
  };
  // 사채서류: 서명완료본(날인본)·수정본이 있으면 그 S3키를 ZIP에 사용 (자동본은 백엔드가 최신 데이터로 재생성)
  const buildBondDocs = (co: CoCard): BondZipBondDoc[] => {
    const out: BondZipBondDoc[] = [];
    for (let i = 0; i < BOND_KEYS.length; i++) {
      const bk = BOND_KEYS[i];
      const vers = getBondVers(p, co, bk);
      const cur = vers[vers.length - 1];
      if (cur && cur.kind !== 'auto' && (cur.s3doc || cur.s3pdf)) {
        out.push({ key: bk, s3doc: cur.s3doc || undefined, s3pdf: cur.s3pdf || undefined });
      }
    }
    return out;
  };
  const downloadZip = async (co: CoCard) => {
    if (zipBusy) return;
    if (!p.master) { toast('공통정보(마스터)가 없습니다 — 상단에서 먼저 입력하세요'); return; }
    setZipBusy(co.id);
    try {
      // 조합 등록원부 — 8단계에 올린 지금 파일을 함께 보낸다(법무사님 발행등기 필수 · 2026-11-02 「ZIP 에 빠진대」)
      const rbName = getUp(p, 's8', '_regbook'); const rbKey = getUpKey(p, 's8', '_regbook');
      const regbook = rbName && rbKey ? { s3Key: rbKey, name: rbName } : null;
      toast((co.name || '법인') + ' 사채서류 ZIP 생성 중 · 구비서류+사채서류' + (regbook ? '+조합 등록원부' : '') + ' (최대 30초)');
      // 조합 저장 폴더로(2026-11-01) — 폴더를 먼저 정하고(처음만 묻기) ZIP 을 만든 뒤 쓴다
      let zipName = (co.name || '법인') + '_사채서류.zip';
      const folder = await saveToProject(p.id, () => zipName, async () => {
        const { url, name } = await bondZip(p.id, co.id, p.master as NonNullable<typeof p.master>, buildAttach(co), buildBondDocs(co), regbook);
        zipName = name;
        const r = await fetch(url); if (!r.ok) throw new Error('ZIP 다운로드 실패 (' + r.status + ')');
        return r.blob();
      });
      toast(savedMsg(folder, zipName) + (folder !== null && !regbook ? ' · 조합 등록원부가 아직 없어 빠졌어요(위 「조합 등록정보」에 올리면 함께 담겨요)' : ''));
    } catch (e) { toast((e as Error).message); } finally { setZipBusy(''); }
  };
  if (!collect) return null;
  const cos = collect.cos;

  const coFile = (co: CoCard, mapKey: string): string => coVal(collect, co, mapKey);
  // 파일 업로드(드래그앤드롭/클릭 공용) + 삭제
  // 저장·다운로드 파일명은 항상 「서류명_법인명(조합명).확장자」 (2026-09-24) — docName·owner 를 주면 올릴 때 이름을 정돈한다
  const upload = async (f0: File, key: string, docName?: string, owner?: string) => {
    try {
      const f = docName ? renameUpload(f0, docName, owner || p.org || '') : f0;
      const r = await s3upload(p.id, f);
      onPatch(mutStore(p, store, (st) => { st.up[key] = r.name; st.upKey = st.upKey || {}; st.upKey[key] = r.key; }));
      toast('업로드됨 · ' + r.name);
    } catch (err) { toast((err as Error).message); }
  };
  const removeUp = (key: string) => onPatch(mutStore(p, store, (st) => { if (st.up) delete st.up[key]; if (st.upKey) delete st.upKey[key]; }));
  const upBody = (key: string, docName?: string, owner?: string) => {
    const cur = getUp(p, store, key);
    const disp = cur && docName ? dispName(cur, docName, owner || p.org || '') : cur;
    return cur ? (
      <div className="s3file ok"><span className="rf" title={cur}>📄 {disp}</span><span className="pill p-green">업로드됨</span><span className="grow" />
        <button className="rbtn" onClick={() => downloadUploadFile(p, getUpKey(p, store, key), disp, toast)}>⬇ 다운로드</button>
        <UpLabel text="교체" onUp={(f) => upload(f, key, docName, owner)} />
        <button className="rbtn danger" title="파일 삭제" onClick={() => removeUp(key)}>🗑 삭제</button></div>
    ) : <UpLabel text="⬆ 업로드 (또는 드래그)" onUp={(f) => upload(f, key, docName, owner)} />;
  };
  const coDocRow = (co: CoCard, slot: string, label: string, mapKey: string, isText?: boolean) => {
    const key = co.id + '_' + slot;
    const st = getStore(p, store);
    const linked = coFile(co, mapKey);
    const src = (st.src && st.src[key]) || (linked ? 'link' : 'upload');
    const sel = (
      <select className="srcsel" value={src} onChange={(e) => onPatch(mutStore(p, store, (s) => { s.src = s.src || {}; s.src[key] = e.target.value; }))}>
        <option value="link">1단계 연동</option>
        <option value="upload">{isText ? '직접 입력' : '직접 업로드'}</option>
      </select>
    );
    let body: React.ReactNode;
    if (src === 'link') {
      body = linked
        ? (isText
          ? <div className="s3file"><span className="rf">{linked}</span><span className="pill p-blue">1단계 연동</span></div>
          : <div className="s3file"><span className="rf">📄 {linked}</span><span className="pill p-blue">1단계 연동</span><span className="grow" /><button className="rbtn" onClick={() => onFilePv(linked, label)}>👁</button><button className="rbtn" onClick={() => downloadCollectFile(p, linked, toast)}>⬇</button></div>)
        : <div className="s3file"><span className="s3muted">1단계 미제출 · &apos;직접 {isText ? '입력' : '업로드'}&apos;로 전환</span></div>;
    } else if (isText) {
      body = <input className="cotext" value={getTxt(p, store, key)} onChange={(e) => onPatch(mutStore(p, store, (s) => { s.txt = s.txt || {}; s.txt[key] = e.target.value; }))} placeholder={label} />;
    } else {
      body = upBody(key, label, co.name);
    }
    return <div className="codoc" key={key}><div className="codoc-h"><span>{label}</span>{sel}</div>{body}</div>;
  };
  const coUploadRow = (co: CoCard, slot: string, label: string, form?: { href: string; name: string }) => {
    const key = co.id + '_' + slot;
    return (
      <div className="codoc" key={key}>
        <div className="codoc-h" style={form ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' } : undefined}>
          <span>{label}</span>
          {form ? <FormDl href={form.href} name={form.name} /> : null}
        </div>
        {upBody(key, label, co.name)}
      </div>
    );
  };
  const toggle = (key: string) => onPatch(mutStore(p, store, (s) => { s.chk = s.chk || {}; s.chk[key] = !s.chk[key]; }));
  const none = (msg: string) => <div className="snote" style={{ padding: '6px 0' }}>{msg}</div>;

  if (n === 8) {
    const regno = getTxt(p, 's8', '_regno');
    const setRegno = (v: string) => onPatch(mutStore(p, 's8', (s) => { s.txt = s.txt || {}; s.txt._regno = v; }));
    // 결성등록일(등록원부 등록일자) — 정기·결산 보고 일정과 감사의견서 면제(결성 3년) 판정이 여기서 나온다 (2026-09-24)
    const regdate = getTxt(p, 's8', '_regdate');
    const setRegdate = (v: string) => onPatch(mutStore(p, 's8', (s) => { s.txt = s.txt || {}; if (v) s.txt._regdate = v; else delete s.txt._regdate; }));
    const regbook = getUp(p, 's8', '_regbook');
    const regbookKey = getUpKey(p, 's8', '_regbook');
    return (
      <div className="sact">
        <div className="sact-h">🧾 전환사채 발행등기</div>
        <MasterCollapse open={masterOpen} onToggle={onToggleMaster} chip={<MasterChip m={p.master} />} title="📋 공통정보 (마스터)" sub="조합원 명부·GP 정보 — 사채서류 자동생성에 사용됩니다">
          <MasterPanel p={p} onPatch={onPatch} toast={toast} withAnswers={false} />
        </MasterCollapse>
        <div className="lbl" style={{ marginTop: 14 }}>조합 등록정보 <span className="s2tag">조합당 1개 · 조합 등록 완료 후 등록원부 발급 · 등록일자는 정기·결산 보고 일정의 기준</span></div>
        <div className="codoc">
          <div className="codoc-h"><span>조합 등록번호 · 등록일자 · 등록원부</span></div>
          <div className="s3file" style={{ gap: 8, flexWrap: 'wrap' }}>
            <input className="cotext" value={regno} onChange={(e) => setRegno(e.target.value)} placeholder="조합 등록번호 (예: 2025-0123)" style={{ maxWidth: 220 }} />
            <input className="cotext" type="date" value={regdate} onChange={(e) => setRegdate(e.target.value)} title="등록원부의 등록일자 (결성등록일)" style={{ maxWidth: 150 }} />
            {!regdate ? <span className="pill p-amber" title="정기·결산 보고 일정 계산에 필요">등록일자 입력</span> : null}
            {/* 등록원부를 보며 등록번호 · 등록일자 적기(2026-11-01 지도사 「이거도 보면서 입력」 — 7단계 고유번호증과 같은 창) */}
            <button className="rbtn" onClick={() => setRegView(regno ? (regdate ? 0 : 1) : 0)} title={regbook ? '등록원부를 보며 등록번호 · 등록일자 적기' : '등록원부를 올리면 보며 적을 수 있습니다'}>📄 등록원부 보며 입력</button>
            <span className="grow" />
            {regbook
              ? (<><span className="pill p-green">원부 업로드됨</span>
                <button className="rbtn" onClick={() => downloadUploadFile(p, regbookKey, dispName(regbook, '조합 등록원부', p.org || ''), toast)}>⬇ 다운로드</button>
                <UpLabel text="교체" onUp={(f) => upload(f, '_regbook', '조합 등록원부')} />
                <button className="rbtn danger" title="등록원부 삭제" onClick={() => removeUp('_regbook')}>🗑 삭제</button></>)
              : <UpLabel text="⬆ 등록원부 업로드 (또는 드래그)" onUp={(f) => upload(f, '_regbook', '조합 등록원부')} />}
          </div>
        </div>
        {regView >= 0 && (
          <DocFieldModal
            title={(p.org || '조합') + ' · 조합 등록원부'}
            fileName={regbook ? dispName(regbook, '조합 등록원부', p.org || '') : ''}
            load={regbook && regbookKey ? () => fileUrl(regbookKey, regbook, true) : undefined}
            noneMsg="오른쪽 「⬆ 등록원부 업로드」로 올리면 여기서 보며 적을 수 있습니다"
            fields={[
              { key: 'regno', label: '조합 등록번호 (등록원부의 「등록번호」)', value: regno, hint: '2025-0123', onChange: setRegno },
              { key: 'regdate', label: '등록일자 (결성등록일)', value: regdate, kind: 'date', onChange: setRegdate },
            ]}
            index={regView}
            onIndex={setRegView}
            onClose={() => setRegView(-1)}
          />
        )}
        <div className="lbl" style={{ marginTop: 14 }}>법인별 구비서류 + 사채서류 자동생성 <span className="s2tag">발행정보·조합원명부 반영</span></div>
        <div className={'cogrid ' + colCls(cos.length)}>
          {cos.length ? cos.map((co) => (
            <div className={'cocol' + (foldOf(co.id) ? ' folded' : '')} key={co.id} id={'co-' + co.id + '-' + n}>
              <div className="cocol-h">🏢 {co.name || '(법인)'}</div>
              <CoStatBar p={p} co={co} n={n} onPatch={onPatch} toast={toast} folded={foldOf(co.id)} onFold={() => toggleFold(co.id)} />
              {foldOf(co.id) ? null : (<>
              {coDocRow(co, 'biz', '사업자등록증', 'co_biz')}
              {coDocRow(co, 'rule', '정관', 'co_rule')}
              {coDocRow(co, 'reg', '법인 등기부등본', 'co_reg')}
              {coDocRow(co, 'fin', '최근연도 재무제표', 'co_fin')}
              {coDocRow(co, 'roster', '주주명부', 'co_roster')}
              <BondForm p={p} co={co} collect={collect} onPatch={onPatch} toast={toast} />
              <div className="lbl" style={{ marginTop: 8 }}>📑 사채서류 (자동 생성 · 날인·다운로드)</div>
              <BondDocRow p={p} co={co} k="bondsub" onPatch={onPatch} toast={toast} />
              <BondDocRow p={p} co={co} k="bondcontract" onPatch={onPatch} toast={toast} />
              <button className="btn pri sm" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} disabled={zipBusy === co.id} onClick={() => downloadZip(co)}>
                {zipBusy === co.id ? '📦 ZIP 생성 중…' : '📦 사채서류 ZIP 다운로드 (법무사 전달용)'}
              </button>
              {/* ZIP 에 무엇이 담기는지 — 조합 등록원부가 없으면 미리 알림(2026-11-02) */}
              {getUp(p, 's8', '_regbook')
                ? <div className="s3muted" style={{ marginTop: 6, textAlign: 'center' }}>구비서류 · 사채서류 · 조합 등록원부를 한 번에 담아요</div>
                : <div className="s3muted" style={{ marginTop: 6, textAlign: 'center', color: 'var(--amber-ink, #B45309)' }}>조합 등록원부가 아직 없어요 — 위 「조합 등록정보」에 올리면 ZIP 에 함께 담겨요</div>}
              </>)}
            </div>
          )) : none('등록된 투자법인이 없습니다 · 1단계에서 추가')}
        </div>
        <div className="snote" style={{ marginTop: 10 }}>
          <a className="upbtn link" href="https://www.iros.go.kr" target="_blank" rel="noopener noreferrer">🔗 인터넷등기소 바로가기</a>
          <span className="s3muted" style={{ marginLeft: 6 }}>법무사 전달 후 발행등기 진행 · 단계 완료는 타임라인의 [완료] 버튼으로</span>
        </div>
      </div>
    );
  }
  if (n === 9) {
    return (
      <div className="sact">
        <div className="sact-h">🏅 벤처기업 확인 신청</div>
        <div className="lbl">법인별 구비서류 · 1단계 연동/직접 입력 · 등기부등본은 전환사채 발행 후 업로드</div>
        <div className={'cogrid ' + colCls(cos.length)}>
          {cos.length ? cos.map((co) => (
            <div className={'cocol' + (foldOf(co.id) ? ' folded' : '')} key={co.id} id={'co-' + co.id + '-' + n}>
              <div className="cocol-h">🏢 {co.name || '(법인)'}</div>
              <CoStatBar p={p} co={co} n={n} onPatch={onPatch} toast={toast} folded={foldOf(co.id)} onFold={() => toggleFold(co.id)} />
              {foldOf(co.id) ? null : (<>
              {coDocRow(co, 'biz', '사업자등록증', 'co_biz')}
              {coDocRow(co, 'idpw', '벤처기업 확인시스템 ID·PW', 'co_idpw', true)}
              {coDocRow(co, 'fin', '재무제표 (3개년)', 'co_fin')}
              {coDocRow(co, 'vat', '최근 부가세 신고서', 'co_vat')}
              {coDocRow(co, 'sme', '중소기업 확인서', 'co_sme')}
              {coUploadRow(co, 'regcb', '등기부등본 (전환사채 발행 후)')}
              </>)}
            </div>
          )) : none('등록된 투자법인이 없습니다')}
        </div>
        <button className="btn pri sm" style={{ width: '100%', justifyContent: 'center', marginTop: 10 }} onClick={() => { window.open('https://www.smes.go.kr/venturein/', '_blank', 'noopener'); toast('벤처확인종합관리시스템(VICS)에서 벤처기업 확인을 신청하세요'); }}>
          <Icon n="send" style={{ width: 13, height: 13 }} /> 벤처기업 확인 신청
        </button>
      </div>
    );
  }
  if (n === 10) {
    return (
      <div className="sact">
        <div className="sact-h">💳 심사비용 결제</div>
        <div className="lbl">법인별 심사비 결제 여부 (클릭하여 체크)</div>
        {cos.length ? cos.map((co) => {
          const done = getChk(p, 's10', co.id);
          return (
            <div className="corow" key={co.id} id={'co-' + co.id + '-' + n}>
              <div className={'chkrow ' + (done ? 'on' : '')} onClick={() => toggle(co.id)}>
                <span className="chkbox">{done ? '✓' : ''}</span><b>{co.name || '(법인)'}</b><span className="grow" />
                <span className="chklabel">심사비 결제 {done ? '완료' : '대기'}</span>
              </div>
              <CoStatBar p={p} co={co} n={n} onPatch={onPatch} toast={toast} />
            </div>
          );
        }) : none('등록된 투자법인이 없습니다')}
      </div>
    );
  }
  return null; // 11~14는 Steps11to14 (D배치 분해 — 구 렌더 제거, 데이터는 s11 저장소에 그대로)
}
